import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";
import { PLANS, formatPrice } from "@/constants/pricing";
import { Check } from "iconoir-react";
import WorkspaceBilling from "@/components/WorkspaceBilling";

let snapScriptPromise = null;

function loadSnap(clientKey, isProduction) {
  if (window.snap) return Promise.resolve();
  if (snapScriptPromise) return snapScriptPromise;
  snapScriptPromise = new Promise((resolve, reject) => {
    const src = isProduction
      ? "https://app.midtrans.com/snap/snap.js"
      : "https://app.sandbox.midtrans.com/snap/snap.js";
    const s = document.createElement("script");
    s.src = src;
    s.setAttribute("data-client-key", clientKey);
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Snap.js"));
    document.body.appendChild(s);
  });
  return snapScriptPromise;
}

export default function Billing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "en";
  const [status, setStatus] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const loadStatus = () => api.get("/billing/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);

  const midtransOk = status?.payments_configured;
  const mayarOk = status?.mayar_configured;
  const ipaymuOk = status?.ipaymu_configured;

  // Default: Mayar (Indonesian, lower fees, magic link). Midtrans is the secondary option.
  const payMayar = async (planKey) => {
    if (!mayarOk) { toast.error("Mayar is not configured"); return; }
    setBusyKey("mayar-" + planKey);
    try {
      const { data } = await api.post("/payments/mayar/create", { plan_key: planKey });
      if (data?.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.error("Mayar did not return a payment link");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mayar checkout failed");
    } finally {
      setBusyKey(null);
    }
  };

  const payMidtrans = async (planKey) => {
    if (!midtransOk) { toast.error(t("billing.not_configured")); return; }
    setBusyKey("midtrans-" + planKey);
    try {
      const { data } = await api.post("/payments/create", { plan_key: planKey });
      await loadSnap(data.client_key, data.is_production);
      window.snap.pay(data.token, {
        onSuccess: () => { toast.success(t("billing.success")); loadStatus(); },
        onPending: () => { toast.message(t("billing.pending")); },
        onError: () => { toast.error(t("billing.failed")); },
        onClose: () => { setBusyKey(null); },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("billing.failed"));
    } finally {
      setBusyKey(null);
    }
  };

  const currentPlan = status?.plan || "free";
  const activeUntil = status?.plan_active_until;

  const payIpaymu = async (planKey) => {
    if (!ipaymuOk) { toast.error("iPaymu is not configured"); return; }
    setBusyKey("ipaymu-" + planKey);
    try {
      const { data } = await api.post("/payments/ipaymu/create", {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      if (data?.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.error("iPaymu did not return a payment link");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "iPaymu checkout failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <AppShell title={t("billing.title")} subtitle={t("billing.subtitle")}>
      <div data-testid="billing-page" className="max-w-5xl">
        <WorkspaceBilling />

        <div className="mt-4 mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Personal account plan (legacy)
        </div>
        <div className="border border-border bg-card p-6 mb-8" data-testid="current-plan-tile">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("billing.current")}</div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="font-marketing text-3xl capitalize" data-testid="current-plan-name">{currentPlan}</span>
            {currentPlan !== "free" && activeUntil && (
              <span className="font-mono text-sm text-muted-foreground">
                {t("billing.active_until")} {String(activeUntil).slice(0, 10)}
              </span>
            )}
            {currentPlan === "free" && (
              <span className="font-mono text-sm text-muted-foreground">{t("billing.free_forever")}</span>
            )}
          </div>
          <p className="mt-3 font-mono text-[10px] text-muted-foreground max-w-prose">
            This is your personal account-level plan. New billing is workspace-scoped — use the Workspace Billing section above.
          </p>
        </div>

        <p className="text-muted-foreground mb-6">Choose a personal-account plan to activate for one month. Payment via Midtrans, iPaymu, or Mayar.id (all Indonesian gateways, IDR).</p>
        {!mayarOk && !midtransOk && (
          <div className="border border-yellow-300 bg-yellow-50 text-yellow-900 p-4 mb-6 font-mono text-xs" data-testid="payments-not-configured">
            {t("billing.not_configured")}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
          {PLANS.map((plan) => {
            const points = t(plan.pointsKey, { returnObjects: true });
            return (
              <div key={plan.key} className={`bg-background p-8 ${plan.featured ? "outline outline-2 -outline-offset-2 outline-intl relative" : ""}`} data-testid={`plan-${plan.key}`}>
                {plan.featured && <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-widest text-intl">{t("pricing.most_popular")}</div>}
                <div className="font-app text-xl">{t(plan.nameKey)}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-marketing text-4xl" data-testid={`plan-price-${plan.key}`}>{formatPrice(plan, lang)}</span>
                  <span className="text-muted-foreground text-sm">{plan.usd === 0 ? t("pricing.per_forever") : t("pricing.per_month")}</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm">
                  {Array.isArray(points) && points.map((pt) => (
                    <li key={pt} className="flex gap-2 items-start"><Check width={16} height={16} className="text-intl shrink-0 mt-0.5" />{pt}</li>
                  ))}
                </ul>
                {plan.payable ? (
                  <div className="mt-8 space-y-2">
                    {/* Default: Mayar */}
                    <button
                      onClick={() => payMayar(plan.key)}
                      disabled={!mayarOk || busyKey === "mayar-" + plan.key}
                      data-testid={`mayar-pay-button-${plan.key}`}
                      className={`w-full py-3 font-app uppercase text-xs font-bold tracking-widest transition-colors disabled:opacity-60 ${plan.featured ? "bg-intl text-white hover:bg-obsidian" : "border border-intl text-intl hover:bg-intl hover:text-white"}`}
                    >
                      {busyKey === "mayar-" + plan.key ? t("billing.processing") : "Bayar dengan Mayar · IDR"}
                    </button>
                    {/* Secondary: Midtrans */}
                    <button
                      onClick={() => payMidtrans(plan.key)}
                      disabled={!midtransOk || busyKey === "midtrans-" + plan.key}
                      data-testid={`pay-button-${plan.key}`}
                      className="w-full py-3 font-app uppercase text-xs font-bold tracking-widest border border-obsidian hover:bg-obsidian hover:text-bone transition-colors disabled:opacity-60"
                    >
                      {busyKey === "midtrans-" + plan.key ? t("billing.processing") : "Midtrans Snap · IDR"}
                    </button>
                    {/* Tertiary: iPaymu (IDR) */}
                    <button
                      onClick={() => payIpaymu(plan.key)}
                      disabled={!ipaymuOk || busyKey === "ipaymu-" + plan.key}
                      data-testid={`ipaymu-pay-button-${plan.key}`}
                      className="w-full py-3 font-app uppercase text-xs font-bold tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors disabled:opacity-60"
                    >
                      {busyKey === "ipaymu-" + plan.key ? "Redirecting…" : "iPaymu · IDR"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-8 w-full py-3 text-center font-mono text-xs uppercase tracking-widest text-muted-foreground border border-dashed border-border" data-testid="plan-free-current">
                    {currentPlan === "free" ? "✓ " + t("billing.current") : t("pricing.free_name")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
